import React, { useState, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const ContactUsForm = () => {
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    phonenumber: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null); // null, 'success', or 'error'
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hcaptchaToken, setHcaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  // Validation functions
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Optional field
    const regex = /^[\d\s\-+()]{7,20}$/;
    return regex.test(phone);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "firstname":
        if (!value.trim()) return "First name is required";
        if (value.trim().length < 2)
          return "First name must be at least 2 characters";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!validateEmail(value)) return "Please enter a valid email address";
        return "";
      case "phonenumber":
        if (value && !validatePhone(value))
          return "Please enter a valid phone number";
        return "";
      case "message":
        if (!value.trim()) return "Message is required";
        if (value.trim().length < 10)
          return "Message must be at least 10 characters";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const onHCaptchaVerify = (token) => {
    setHcaptchaToken(token);
    if (errors.captcha) {
      setErrors((prev) => ({ ...prev, captcha: "" }));
    }
  };

  const onHCaptchaExpire = () => {
    setHcaptchaToken(null);
  };

  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    const enableCaptcha = process.env.REACT_APP_ENABLE_CAPTCHA !== "false";

    if (enableCaptcha && !hcaptchaToken) {
      newErrors.captcha = "Please complete the captcha verification";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setResult(null);
    setErrorMessage("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          hcaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResult("success");
        setFormData({
          firstname: "",
          lastname: "",
          phonenumber: "",
          email: "",
          message: "",
        });
        setHcaptchaToken(null);
        captchaRef.current?.resetCaptcha();
      } else {
        setResult("error");
        setErrorMessage(
          data.error || "Failed to send message. Please try again.",
        );
        captchaRef.current?.resetCaptcha();
        setHcaptchaToken(null);
      }
    } catch (error) {
      console.error("Error:", error);
      setResult("error");
      setErrorMessage(
        "Network error. Please check your connection and try again.",
      );
      captchaRef.current?.resetCaptcha();
      setHcaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={sendEmail} noValidate>
      <div className="container">
        <div className="row">
          <div className="col-md-6 col-sm-6 col-xs-12">
            <div
              className={`single-input-field ${errors.firstname ? "has-error" : ""}`}
            >
              <input
                placeholder="First Name *"
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
              />
              {errors.firstname && (
                <span className="error-text">{errors.firstname}</span>
              )}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-xs-12">
            <div className="single-input-field">
              <input
                placeholder="Last Name"
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <div className="row">
          <div className="col-md-6 col-sm-6 col-xs-12">
            <div
              className={`single-input-field ${errors.phonenumber ? "has-error" : ""}`}
            >
              <input
                placeholder="Phone"
                type="tel"
                name="phonenumber"
                value={formData.phonenumber}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
              />
              {errors.phonenumber && (
                <span className="error-text">{errors.phonenumber}</span>
              )}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-xs-12">
            <div
              className={`single-input-field ${errors.email ? "has-error" : ""}`}
            >
              <input
                placeholder="Your E-mail *"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loading}
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-12 message-input">
        <div
          className={`single-input-field ${errors.message ? "has-error" : ""}`}
        >
          <textarea
            placeholder="Enter Your Message Here *"
            name="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={loading}
          />
          {errors.message && (
            <span className="error-text">{errors.message}</span>
          )}
        </div>
      </div>

      {/* hCaptcha (Conditional) */}
      {process.env.REACT_APP_ENABLE_CAPTCHA !== "false" && (
        <div className="col-md-12">
          <div
            className={`captcha-container ${errors.captcha ? "has-error" : ""}`}
          >
            <HCaptcha
              ref={captchaRef}
              sitekey={
                process.env.REACT_APP_HCAPTCHA_SITE_KEY ||
                "10000000-ffff-ffff-ffff-000000000001"
              }
              onVerify={onHCaptchaVerify}
              onExpire={onHCaptchaExpire}
            />
            {errors.captcha && (
              <span className="error-text">{errors.captcha}</span>
            )}
          </div>
        </div>
      )}

      <div className="single-input-fieldsbtn">
        <button
          type="submit"
          disabled={loading}
          className={loading ? "loading" : ""}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Sending...
            </>
          ) : (
            "Send Now"
          )}
        </button>
      </div>

      {result === "success" && (
        <div className="success-send-msg">
          Thank you for contacting Iron Wall X! Your message has been received.
          Our team will respond within 24 hours.
        </div>
      )}

      {result === "error" && (
        <div className="error-send-msg">{errorMessage}</div>
      )}
    </form>
  );
};

export default ContactUsForm;
